/* eslint-disable @typescript-eslint/no-explicit-any */
import BackspaceIcon from '@mui/icons-material/Backspace';
import EditIcon from '@mui/icons-material/Edit';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import PanToolIcon from '@mui/icons-material/PanTool';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridValueGetterParams
} from '@mui/x-data-grid-premium';
import { DataGridCell, baseCell, booleanCell, dateCell } from 'components/cells';
import { filterOperators, dateFilterOperators } from 'components/enhanced';
import { useTranslation } from 'react-i18next';
import { IsTenantAdministrator, IsTenantCoordinator } from 'shared/permissions';

export function AutomaticPurchaseLinesColumns(
  reject,
  approve,
  preApprove,
  handleDelete,
  handleEdit,
  handleApproveAll,
  handleRejectedAll
): GridColDef[] {
  const { t } = useTranslation();

  const columns: GridColDef[] = [
    {
      field: 'index',
      headerName: t('global.count'),
      width: 50,
      filterable: false,
      sortable: false
    },
    {
      field: 'is_periodic',
      headerName: t(`global.is_periodic`),
      width: 50,
      // v6: valueGetter usa GridValueGetterParams, non GridRenderCellParams
      valueGetter: (params: GridValueGetterParams) => params.row.is_periodic,
      // v6: GridRenderCellParams<RowType, ValueType>; params.value qui è booleano
      renderCell: (params: GridRenderCellParams<any, boolean>) =>
        params.value ? (
          <Tooltip title="is_periodic" placement="top">
            <EventRepeatIcon color="info" />
          </Tooltip>
        ) : null
    },
    {
      field: 'status',
      headerName: t(`entities.purchase_request.status-field`),
      width: 100,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <DataGridCell content={t(`entities.purchase_request.status.s-${params.value}`)} />
      )
    },
    {
      field: 'product',
      headerName: t('global.product'),
      width: 200,
      filterOperators,
      valueGetter: (params) => params.row.product?.code || '', // Usa solo `code`
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <DataGridCell content={params.value} />
      )
    },
    {
      field: 'description',
      headerName: t(`entities.product.description`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'order_notes',
      headerName: t(`entities.product.order_notes`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'contract',
      headerName: t(`global.contract`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'quantity',
      headerName: t(`entities.purchase_request.quantity`),
      width: 100,
      type: 'number',
      renderCell: baseCell
    },
    {
      field: 'discounted',
      headerName: t('entities.movement.discounted'),
      width: 100,
      type: 'boolean',
      renderCell: booleanCell(t)
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'requested_shipment_date',
      headerName: t(`entities.purchase_request.requested_shipment_date`),
      width: 200,
      filterOperators: dateFilterOperators,
      type: 'date',
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      renderCell: dateCell
    },
    {
      field: 'notes',
      headerName: t(`entities.purchase_request.notes`),
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'actions',
      type: 'actions',
      renderHeader: () => (
        <IsTenantAdministrator>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Tooltip title={t('actions.disapprove-all')}>
              <IconButton onClick={() => handleRejectedAll()} size="small">
                <PanToolIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('actions.approve-all')} placement="top">
              <IconButton onClick={() => handleApproveAll()} size="small">
                <VerifiedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </IsTenantAdministrator>
      ),
      width: 150,
      getActions: (params: GridRowParams) => [
        <Tooltip key={1} title={t('actions.disapprove')} placement="top">
          <IconButton id="Reject" onClick={() => reject(params.row)}>
            <PanToolIcon
              fontSize="small"
              color={params.row.status === 4 ? 'warning' : 'disabled'}
            />
          </IconButton>
        </Tooltip>,
        <IsTenantCoordinator key={2}>
          <Tooltip title={t('actions.pre-approve')} placement="top">
            <IconButton onClick={() => preApprove(params.row)} id="Pre-approve">
              <ThumbUpIcon fontSize="small" color={params.row.status === 2 ? 'info' : 'disabled'} />
            </IconButton>
          </Tooltip>
        </IsTenantCoordinator>,
        <IsTenantAdministrator key={3}>
          <Tooltip title={t('actions.approve')} placement="top">
            <IconButton onClick={() => approve(params.row)} id="Approve">
              <VerifiedIcon
                fontSize="small"
                color={params.row.status === 3 ? 'success' : 'disabled'}
              />
            </IconButton>
          </Tooltip>
        </IsTenantAdministrator>,
        ...(params.row.id
          ? [
              <Tooltip key={4} title={t('actions.delete')} placement="top">
                <IconButton onClick={() => handleDelete(params.row)} size="small">
                  <BackspaceIcon fontSize="small" />
                </IconButton>
              </Tooltip>,
              <Tooltip key={5} title={t('actions.edit')} placement="top">
                <IconButton onClick={() => handleEdit(params.row)} size="small">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ]
          : [])
      ]
    } as GridColDef
  ];
  return columns;
}
